import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Package, ArrowRight, Shield, TrendingUp, Percent } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientProducts() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, [token]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskBadge = (level) => {
    const styles = {
      low: 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30',
      medium: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30',
      high: 'bg-red-500/20 text-red-500 border-red-500/30'
    };
    return <Badge className={`${styles[level]} border`}>{t(level)}</Badge>;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'epargne_securisee': return <Shield className="w-5 h-5" />;
      case 'investissement_long_terme': return <TrendingUp className="w-5 h-5" />;
      case 'crypto': return <Package className="w-5 h-5" />;
      default: return <Package className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t('savings_products')}
            </h2>
            <p className="text-muted-foreground text-sm mt-1">
              Découvrez nos produits d'épargne et d'investissement
            </p>
          </div>
        </div>

        {products.length === 0 ? (
          <Card className="card-premium">
            <CardContent className="py-12 text-center">
              <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucun produit disponible</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map(product => (
              <Card key={product.id} className="card-premium group" data-testid={`product-${product.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37]">
                      {getCategoryIcon(product.category)}
                    </div>
                    {getRiskBadge(product.risk_level)}
                  </div>
                  
                  <h3 className="font-semibold text-lg mb-1">{product.name}</h3>
                  <p className="text-xs text-muted-foreground capitalize mb-4">
                    {product.category?.replace(/_/g, ' ')}
                  </p>
                  
                  <p className="text-sm text-muted-foreground mb-6 line-clamp-2">
                    {product.description}
                  </p>

                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground flex items-center gap-1">
                        <Percent className="w-4 h-4" />
                        {t('return_rate')}
                      </span>
                      <span className="text-[#D4AF37] font-bold">{product.return_rate}%</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('min_investment')}</span>
                      <span>€{product.min_investment?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Capital garanti</span>
                      <span>{product.capital_guaranteed ? 'Oui' : 'Non'}</span>
                    </div>
                  </div>

                  <Link to={`/client/products/${product.id}`}>
                    <Button className="w-full btn-gold group-hover:shadow-[0_0_25px_rgba(212,175,55,0.5)]">
                      Découvrir
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
